import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { 
  Shield, 
  ChevronDown, 
  CheckCircle, 
  XCircle, 
  Info,
  AlertCircle,
  Monitor,
  ChevronRight,
  Terminal
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { 
  Card, 
  CardHeader, 
  CardTitle, 
  CardContent 
} from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { supabase } from "@/integrations/supabase/client";

type ComplianceRule = {
  id: string;
  rule_number: string;
  title: string;
  description: string | null;
  remediation: string | null;
  category: string;
  level: number;
  status: "compliant" | "non_compliant" | "not_checked";
  os_version: string[];
};

type GroupedRules = {
  [category: string]: ComplianceRule[];
};

const CisLevel1Rules = () => {
  const [openCategories, setOpenCategories] = useState<string[]>([]);
  const [openDetails, setOpenDetails] = useState<string[]>([]);

  const { data: rules, isLoading } = useQuery({
    queryKey: ["cis-level1-rules"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cis_compliance_rules")
        .select("*")
        .eq("level", 1)
        .order("category", { ascending: true });

      if (error) throw error;
      return data as ComplianceRule[];
    },
  });

  const toggleCategory = (category: string) => {
    setOpenCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category]
    );
  };

  const toggleDetails = (ruleId: string) => {
    setOpenDetails((prev) =>
      prev.includes(ruleId)
        ? prev.filter((id) => id !== ruleId)
        : [...prev, ruleId]
    );
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "compliant":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "non_compliant":
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  const getProgressValue = (rules: ComplianceRule[]) => {
    const compliantCount = rules.filter(
      (rule) => rule.status === "compliant"
    ).length;
    return (compliantCount / rules.length) * 100;
  };

  const groupedRules = rules?.reduce((acc: GroupedRules, rule) => {
    // Ensure we're using the updated category name
    const category = rule.category === "Audit Configuration" ? "Auditing" : rule.category;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(rule);
    return acc;
  }, {});

  if (isLoading) {
    return (
      <Card className="w-full max-w-4xl mx-auto">
        <CardContent className="p-6">
          <div className="flex items-center space-x-4">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
            <p>Loading CIS Level 1 rules...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Collapsible className="w-full max-w-4xl mx-auto">
      <Card className="w-full">
        <CollapsibleTrigger className="w-full">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 hover:bg-gray-50 transition-colors">
            <div className="space-y-1">
              <CardTitle className="text-xl font-bold flex items-center gap-2">
                <Shield className="h-5 w-5 text-blue-500" />
                CIS Level 1 Benchmark
              </CardTitle>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Monitor className="h-4 w-4" />
                  <span>macOS</span>
                </div>
                <span className="text-sm font-medium">
                  {rules?.length || 0} rules
                </span>
              </div>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform data-[state=open]:rotate-90" />
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-4">
            <div className="space-y-4">
              {groupedRules &&
                Object.entries(groupedRules).map(([category, categoryRules]) => (
                  <div key={category} className="rounded-lg border bg-card">
                    <div className="p-6">
                      <Button
                        variant="ghost"
                        className="flex w-full items-center justify-between p-0 hover:bg-transparent"
                        onClick={() => toggleCategory(category)}
                      >
                        <div className="flex items-center space-x-4">
                          <div
                            className={cn(
                              "flex h-10 w-10 shrink-0 items-center justify-center rounded-full",
                              getProgressValue(categoryRules) === 100
                                ? "bg-green-100"
                                : getProgressValue(categoryRules) > 0
                                ? "bg-yellow-100"
                                : "bg-gray-100"
                            )}
                          >
                            <Shield
                              className={cn(
                                "h-5 w-5",
                                getProgressValue(categoryRules) === 100
                                  ? "text-green-500"
                                  : getProgressValue(categoryRules) > 0
                                  ? "text-yellow-500"
                                  : "text-gray-500"
                              )}
                            />
                          </div>
                          <div className="space-y-1 text-left">
                            <h3 className="font-semibold leading-none tracking-tight">
                              {category}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              {categoryRules.length} rules
                            </p>
                          </div>
                        </div>
                        <ChevronDown
                          className={cn(
                            "h-4 w-4 shrink-0 transition-transform duration-200",
                            openCategories.includes(category) && "rotate-180"
                          )}
                        />
                      </Button>
                      {openCategories.includes(category) && (
                        <div className="pt-6">
                          <Separator className="mb-6" />
                          <div className="space-y-6">
                            {categoryRules.map((rule, index) => (
                              <div key={rule.id} className="space-y-2">
                                <div 
                                  className="flex items-start justify-between cursor-pointer"
                                  onClick={() => toggleDetails(rule.id)}
                                >
                                  <div className="space-y-1">
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium leading-none min-w-[2rem]">
                                        {index + 1}.
                                      </span>
                                      <div className="flex items-center gap-2">
                                        {getStatusIcon(rule.status)}
                                        <h4 className="font-medium leading-none">
                                          {rule.title}
                                        </h4>
                                      </div>
                                    </div>
                                  </div>
                                  <ChevronDown
                                    className={cn(
                                      "h-4 w-4 shrink-0 transition-transform duration-200",
                                      openDetails.includes(rule.id) && "rotate-180"
                                    )}
                                  />
                                </div>
                                
                                {openDetails.includes(rule.id) && (
                                  <div className="mt-4 space-y-4 bg-slate-50 p-4 rounded-lg">
                                    {rule.description && (
                                      <div>
                                        <h5 className="text-sm font-medium mb-2">Description</h5>
                                        <p className="text-sm text-muted-foreground">
                                          {rule.description}
                                        </p>
                                      </div>
                                    )}
                                    
                                    {rule.remediation && (
                                      <div>
                                        <h5 className="text-sm font-medium mb-2 flex items-center gap-2">
                                          <Terminal className="h-4 w-4" />
                                          Remediation Command
                                        </h5>
                                        <pre className="bg-slate-900 text-slate-50 p-3 rounded-md text-sm font-mono overflow-x-auto">
                                          {rule.remediation}
                                        </pre>
                                      </div>
                                    )}
                                  </div>
                                )}
                                
                                <Progress
                                  value={rule.status === "compliant" ? 100 : 0}
                                  className={cn(
                                    rule.status === "compliant" &&
                                      "bg-green-100 [&>div]:bg-green-500",
                                    rule.status === "non_compliant" &&
                                      "bg-red-100 [&>div]:bg-red-500",
                                    rule.status === "not_checked" &&
                                      "bg-gray-100 [&>div]:bg-gray-500"
                                  )}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

export default CisLevel1Rules;